/**
 * useListDeleteHandler — pattern condiviso per la cancellazione di una riga
 * da una lista paginata server-side.
 *
 * Risolve due bug correlati che apparivano in tutte le tabelle paginate
 * (Task/Business/Person/Queue, vedi TS-9):
 *   1) il `totalCount` mostrato dal DataGrid restava sballato dopo la
 *      cancellazione (il reducer `remove.fulfilled` rimuoveva l'item da
 *      `state.items` ma non decrementava `state.totalCount`);
 *   2) se l'utente cancellava tutte le righe della pagina corrente mentre
 *      esistevano altre pagine con elementi, il DataGrid mostrava
 *      "nessun risultato" senza navigare indietro o rifettchare.
 *
 * La strategia: dopo `delete.fulfilled`, rifettchare la pagina con i
 * `fetchParams` correnti. Il server è la fonte di verità — porta il
 * `totalCount` corretto E ripopola la pagina con elementi spostati dalle
 * pagine successive. Trade-off: 1 request HTTP in più per ogni delete in
 * cambio di coerenza state ↔ server.
 *
 * Comportamento:
 *   - `await dispatch(deleteThunk(arg)).unwrap()` propaga eventuali errori
 *     al chiamante (toast).
 *   - Se la pagina sta per svuotarsi (`itemsLength === 1`) E non siamo già
 *     in `page === 0` → `resetPage()`. Il `useEffect` esistente nei List
 *     monitora `backendParams` (che include `page`) e auto-triggera il
 *     fetch della nuova pagina. Niente double-fetch.
 *   - Altrimenti → `dispatch(fetchThunk(fetchParams))` esplicito per
 *     rifettchare la pagina corrente.
 */

import { useCallback } from 'react';

/** Action-like generic: il chiamante ne passa il vero tipo opaco. */
type AnyAction = unknown;
/** Dispatch generic: accetta thunk-like e plain action. */
type AnyDispatch = (action: AnyAction) => unknown;

export interface UseListDeleteHandlerOpts<TArg, TFetchParams> {
  /** Thunk RTK che cancella un record. Riceve l'argomento (id tipicamente). */
  deleteThunk: (arg: TArg) => AnyAction;
  /** Thunk RTK che fa fetch della lista paginata. Riceve i params correnti. */
  fetchThunk: (params: TFetchParams) => AnyAction;
  /** Params correnti di paginazione/sort/search da passare a fetchThunk. */
  fetchParams: TFetchParams;
  /**
   * Lunghezza corrente di items visibili nella pagina (usato per decidere
   * se la pagina sta per svuotarsi e va decrementata). Tipicamente
   * `state.items.length`.
   */
  itemsLength: number;
  /** Page 0-based MUI DataGrid corrente (= `paginationModel.page`). */
  page: number;
  /** Callback per resettare la pagina a 0 (esposta da useServerListParams). */
  resetPage: () => void;
  /** Dispatch Redux del consumer. */
  dispatch: AnyDispatch;
}

export function useListDeleteHandler<TArg, TFetchParams>(
  opts: UseListDeleteHandlerOpts<TArg, TFetchParams>,
): (arg: TArg) => Promise<void> {
  const {
    deleteThunk,
    fetchThunk,
    fetchParams,
    itemsLength,
    page,
    resetPage,
    dispatch,
  } = opts;

  return useCallback(
    async (arg: TArg): Promise<void> => {
      const dispatchResult = dispatch(deleteThunk(arg));
      // `dispatch(thunk)` di RTK ritorna una Promise con `.unwrap()`. Se per
      // qualsiasi motivo il caller passa un dispatch "plain" senza thunk
      // middleware, il check evita NPE.
      if (
        dispatchResult !== null &&
        typeof dispatchResult === 'object' &&
        'unwrap' in dispatchResult &&
        typeof (dispatchResult as { unwrap: unknown }).unwrap === 'function'
      ) {
        await (dispatchResult as { unwrap: () => Promise<unknown> }).unwrap();
      }
      if (itemsLength <= 1 && page > 0) {
        // Pagina sta per svuotarsi: torna a page=0. Il useEffect sul
        // backendParams del componente triggerera' un fetch della nuova
        // pagina automaticamente.
        resetPage();
      } else {
        // Pagina rimane non-vuota o siamo gia' in pagina 0: rifetto
        // esplicito per riallineare totalCount e cattura elementi shiftati.
        dispatch(fetchThunk(fetchParams));
      }
    },
    [deleteThunk, fetchThunk, fetchParams, itemsLength, page, resetPage, dispatch],
  );
}
